import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Check, ChevronLeft, ChevronRight, Copy, X } from 'lucide-react'
import { patternStyle, safeColor } from '@/lib/promoStyle'
import { copyCouponCode, formatThaiDate, setPendingCoupon } from '@/lib/promoCoupon'
import { useAuthStore } from '@/store/authStore'
import type { PopupItem } from '@/services/campaignService'

// ป๊อปอัปต้อนรับ — ขึ้นเฉพาะ "หน้าแรก" และเฉพาะผู้ที่ **ล็อกอินแล้ว** (ยังไม่ล็อกอิน = ไม่ขึ้น · แถบ/ปุ่มลอยของแคมเปญแสดงให้ทุกคนเห็นตามปกติ)
// กติกา "เห็นแล้ว": ขึ้นใหม่ทุกครั้งที่ล็อกอินใหม่ (scope = loginId ของรอบนั้น) ปิดแล้วไม่ขึ้นอีกจนกว่าจะล็อกอินครั้งถัดไป
//   · แอดมินแก้เนื้อหาป๊อปอัป (version เปลี่ยน) → ขึ้นอีกครั้ง
// ไม่เกี่ยวกับแคมเปญ: แอดมินติ๊ก "แสดงเป็นป๊อปอัปที่หน้าแรก" ที่คูปองกี่ใบก็ได้ · หลายใบ = หน้าต่างเดียวเลื่อนดูทีละใบ
const seenKey = (p: PopupItem, scope: string) => `promoPopupSeen:${scope}:${p.coupon_id}:${p.version ?? ''}`
const wasSeen = (p: PopupItem, scope: string) => { try { return localStorage.getItem(seenKey(p, scope)) === '1' } catch { return false } }
const markSeen = (items: PopupItem[], scope: string) => { try { items.forEach((p) => localStorage.setItem(seenKey(p, scope), '1')) } catch { /* ไม่มี storage = ปิดเฉพาะครั้งนี้ */ } }

const SHOW_DELAY_MS = 1200   // รอให้หน้าแรกขึ้นก่อน ค่อยเด้ง

export default function PromoPopup({ popups }: { popups: PopupItem[] }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  // scope = รหัสรอบการล็อกอิน (null = ยังไม่ล็อกอิน → ไม่แสดงป๊อปอัป) — เปลี่ยนเมื่อล็อกอินใหม่โดยไม่ต้องรีโหลดหน้า รายการ "ที่ยังไม่เคยเห็น" จึงคำนวณใหม่ตามรอบนั้น
  const scope = useAuthStore((s) => (s.isLoggedIn ? s.loginId : null))
  // seen = ใบที่ปิดไปแล้วในรอบนี้ (state) — ต้องมี เพราะ localStorage ไม่ทำให้ React คำนวณ "ที่ยังไม่เคยเห็น" ใหม่
  // (ถ้าไม่มี หลังปิดแล้วรายการเดิมยังถูกนับว่าไม่เคยเห็น และป๊อปอัปเด้งซ้ำ) และกันกรณีเบราว์เซอร์ไม่มี storage
  const [seen, setSeen] = useState<Set<string>>(() => new Set())
  const unseen = useMemo(
    () => (scope ? popups.filter((p) => !seen.has(seenKey(p, scope)) && !wasSeen(p, scope)) : []),
    [popups, seen, scope]
  )
  const [open, setOpen] = useState(false)
  const [snapshot, setSnapshot] = useState<PopupItem[]>([])   // รายการที่กำลังโชว์ (ไม่เปลี่ยนตามการ refresh ระหว่างที่เปิดอยู่)
  const [index, setIndex] = useState(0)
  const [copied, setCopied] = useState(false)

  // เปิดครั้งเดียวต่อการเข้าหน้าแรก เมื่อล็อกอินอยู่และมีป๊อปอัปที่ยังไม่เคยเห็นในรอบนี้
  useEffect(() => {
    if (pathname !== '/' || open || unseen.length === 0) return
    const t = setTimeout(() => { setSnapshot(unseen); setIndex(0); setOpen(true) }, SHOW_DELAY_MS)
    return () => clearTimeout(t)
  }, [pathname, unseen, open])

  // ล็อกเอาต์ระหว่างที่ป๊อปอัปเปิดอยู่ → ปิดทันที
  useEffect(() => {
    if (!scope && open) { setOpen(false); setSnapshot([]) }
  }, [scope, open])

  const close = () => {
    if (scope) {
      markSeen(snapshot, scope)   // ปิด (✕ / ไว้ภายหลัง / คลิกนอกกรอบ / Esc) = ถือว่าเห็นทุกใบที่โชว์ ไม่ขึ้นซ้ำในรอบล็อกอินนี้
      setSeen((prev) => new Set([...prev, ...snapshot.map((p) => seenKey(p, scope))]))
    }
    setOpen(false)
    setSnapshot([])
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, snapshot, scope])

  if (!open || snapshot.length === 0) return null
  const p = snapshot[Math.min(index, snapshot.length - 1)]
  const color = safeColor(p.color, '#5B52E5')
  const until = p.expires_at ? `ใช้ได้ถึงวันที่ ${formatThaiDate(p.expires_at)} เท่านั้น` : ''

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]" onClick={close} role="dialog" aria-modal="true">
      <div className="relative w-full max-w-[380px] overflow-hidden rounded-[28px] bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* หัว: ลายพื้นหลัง + ป้าย + หัวใหญ่ + คำบรรยาย */}
        <div className="relative px-6 pb-24 pt-7 text-center text-white" style={patternStyle(p.pattern, color)}>
          <button type="button" onClick={close} aria-label="ปิด" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/70 text-white transition hover:bg-white/20">
            <X className="h-4 w-4" />
          </button>
          {p.badge && <span className="inline-block rounded-full bg-white/20 px-4 py-1 text-[11px] font-bold tracking-widest">{p.badge}</span>}
          <p className="mt-3 text-6xl font-extrabold leading-none tracking-tight sm:text-7xl">{p.headline}</p>
          {p.subtitle && <p className="mt-3 text-lg font-bold leading-snug">{p.subtitle}</p>}
        </div>

        {/* กล่องส่วนลด (ตัวเลขมาจากคูปองจริง) */}
        <div className="relative -mt-16 mx-6 rounded-2xl bg-white px-4 py-4 text-center shadow-lg">
          <p className="text-sm text-text-muted">ส่วนลดพิเศษ</p>
          <p className="text-6xl font-extrabold leading-tight" style={{ color }}>{p.discount}</p>
          {p.note && <p className="mt-1 text-sm text-text-muted">{p.note}</p>}
        </div>

        <div className="px-6 pb-6 pt-5">
          {/* รหัส + คัดลอก */}
          <div className="flex overflow-hidden rounded-full border-2 border-dashed" style={{ borderColor: color }}>
            <div className="flex flex-1 items-center gap-2 px-4 py-2.5 text-sm">
              <span className="text-text-muted">ใช้โค้ด</span>
              <span className="font-mono text-base font-bold tracking-wide text-text">{p.code}</span>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (await copyCouponCode(p.code)) {
                  setCopied(true)
                  setTimeout(() => setCopied(false), 1600)
                }
              }}
              className="flex items-center gap-1.5 px-5 text-sm font-bold text-white transition hover:opacity-90"
              style={{ background: color }}
            >
              {copied ? <><Check className="h-4 w-4" />คัดลอกแล้ว</> : <><Copy className="h-4 w-4" />คัดลอก</>}
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setPendingCoupon(p.code)   // ไปวางให้ที่ช่องคูปองหน้าชำระเงิน
              close()
              navigate('/courses')
            }}
            className="mt-4 block w-full rounded-full py-3.5 text-center text-base font-bold text-white shadow-lg transition hover:opacity-90"
            style={{ background: color }}
          >
            เลือกคอร์สเลย
          </button>

          {until && <p className="mt-4 text-center text-sm text-text-muted">{until}</p>}

          {/* หลายใบ: เลื่อนดูทีละใบ */}
          {snapshot.length > 1 && (
            <div className="mt-3 flex items-center justify-center gap-3">
              <button type="button" aria-label="ใบก่อนหน้า" disabled={index === 0} onClick={() => setIndex((i) => Math.max(i - 1, 0))} className="rounded-full p-1 text-text-muted transition hover:bg-surface-2 disabled:opacity-30">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex gap-1.5">
                {snapshot.map((s, i) => (
                  <button key={s.coupon_id} type="button" aria-label={`ใบที่ ${i + 1}`} onClick={() => setIndex(i)} className="h-2 rounded-full transition-all" style={{ width: i === index ? 20 : 8, background: i === index ? color : '#D1D5DB' }} />
                ))}
              </div>
              <button type="button" aria-label="ใบถัดไป" disabled={index === snapshot.length - 1} onClick={() => setIndex((i) => Math.min(i + 1, snapshot.length - 1))} className="rounded-full p-1 text-text-muted transition hover:bg-surface-2 disabled:opacity-30">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}

          <button type="button" onClick={close} className="mt-3 block w-full text-center text-sm text-text-muted transition hover:text-text">
            ไว้ภายหลัง
          </button>
        </div>
      </div>
    </div>
  )
}
